import { RefObject, useImperativeHandle, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  Divider,
  Group,
  Modal,
  NumberInput,
  Radio,
  Stack,
  Text,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { addDays, format } from 'date-fns';
import type { HighwayMaintenanceFeePenaltyTicketSummary } from '#/components/ui/tickets/highway-maintenance-fee-penalty/model';
import { TICKET_TYPE, TICKET_TYPE_CONFIG } from '../../../config';
import type {
  ChargeFailDialogConfirmValue,
  ChargeFailDialogRef,
} from './useChargeFailDialog';

const CONFIG =
  TICKET_TYPE_CONFIG[TICKET_TYPE.HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKETS];

export function ChargeFailDialog({
  modalRef,
}: {
  modalRef: RefObject<ChargeFailDialogRef | null>;
}) {
  const [modelState, setModelState] = useState<
    | {
        amount: number;
        ticket: HighwayMaintenanceFeePenaltyTicketSummary;
        onConfirm: () => void;
        onCancel: () => void;
      }
    | undefined
  >();

  const [days, setDays] = useState<number | undefined>(7);

  useImperativeHandle(
    modalRef,
    () => ({
      days,
      confirm: async (
        ticket: HighwayMaintenanceFeePenaltyTicketSummary,
        amount: number,
      ) => {
        const promise = new Promise<ChargeFailDialogConfirmValue | undefined>(
          (resolve) => {
            setModelState({
              amount,
              ticket,
              onConfirm: () =>
                resolve({ afterDays: modalRef.current?.days ?? 0 }),
              onCancel: () => resolve(undefined),
            });
          },
        );
        const result = await promise;
        setModelState(undefined);
        setDays(7);
        return result;
      },
    }),
    [modalRef, days],
  );

  if (!modelState) return null;

  const { ticket, onConfirm, onCancel, amount } = modelState;

  const scheduledDate = days ? addDays(new Date(), days) : undefined;
  const scheduledDateText = scheduledDate
    ? format(scheduledDate, 'yyyy/MM/dd')
    : undefined;
  const isRetry = days !== undefined && days > 0;

  return (
    <Modal
      opened={!!modelState}
      onClose={onCancel}
      styles={{ title: { width: '100%' } }}
      title={
        <Text fw={600} size="lg">
          請款結果
        </Text>
      }
      size="lg"
    >
      <Stack gap="md">
        <Stack gap="5">
          <Text size={'10px'} c="dimmed">
            {`${ticket.id}．${CONFIG.name}．${ticket.account.email}．${ticket.plateNumber}`}
          </Text>
        </Stack>
        <Alert color="red" title="請款失敗" icon={<IconX size={16} />}>
          <Text size="sm">{`金額 NT$ ${amount}`}</Text>
          <Text size="xs" c="dimmed" mt={4}>
            本任務將以「請款失敗」狀態進入歷史任務，系統將寄信引導用戶更新付款方式
          </Text>
        </Alert>

        <Stack gap="xs">
          <Text size="sm" fw={500}>
            後續處理{' '}
            <Text component="span" c="red">
              *
            </Text>
          </Text>
          <Radio.Group
            value={isRetry ? 'retry' : 'close_case'}
            onChange={(v) => setDays(v === 'retry' ? 7 : undefined)}
          >
            <Stack gap="sm">
              <Stack gap="xs">
                <Radio
                  value="retry"
                  label="幾天後重新查詢"
                  description="系統將在指定天數後重新產生待查詢任務"
                />
                <Collapse expanded={isRetry}>
                  <Stack
                    gap={4}
                    pl="md"
                    ml="xs"
                    style={{
                      borderLeft: '2px solid var(--mantine-color-gray-3)',
                    }}
                  >
                    <NumberInput
                      label={
                        <Text size="sm" fw={500}>
                          X 天後重新產生待查詢任務{' '}
                          <Text component="span" c="red">
                            *
                          </Text>
                        </Text>
                      }
                      value={days ?? 7}
                      onChange={(v) => setDays(typeof v === 'number' ? v : 7)}
                      min={1}
                      max={90}
                    />
                    {scheduledDateText && (
                      <Text size="xs" c="dimmed">
                        {`預計於 ${scheduledDateText} 自動產生新任務`}
                      </Text>
                    )}
                  </Stack>
                </Collapse>
              </Stack>
              <Radio
                value="close_case"
                label="不重新嘗試"
                description="本任務直接結案，不再追蹤"
              />
            </Stack>
          </Radio.Group>
        </Stack>

        <Divider />

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onCancel}>
            關閉
          </Button>
          <Button color="red" onClick={onConfirm}>
            確認結案
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
